import { readWorkstationBinding } from './workstationIdentity';

const LOCAL_API_URL = 'http://localhost:3001';
const CAPACITOR_SERVER_API_URL = 'https://beborne.com';

export function getApiBaseUrl() {
  const configuredUrl = process.env.REACT_APP_API_URL;
  if (configuredUrl) {
    return configuredUrl.replace(/\/$/, '');
  }

  if (typeof window === 'undefined') {
    return LOCAL_API_URL;
  }

  const { protocol, hostname, port } = window.location;
  const isCapacitorRuntime = Boolean(window.Capacitor?.isNativePlatform?.());
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    if (isCapacitorRuntime) {
      return CAPACITOR_SERVER_API_URL;
    }
    return LOCAL_API_URL;
  }

  if (port === '3000') {
    return `${protocol}//${hostname}:3001`;
  }

  // Force HTTPS on production domain to avoid mixed auth/storage contexts
  // and browser "Non sécurisé" state when users type http:// manually.
  if (hostname === 'beborne.com' && protocol !== 'https:') {
    return `https://${hostname}`;
  }

  return `${protocol}//${hostname}`;
}

export const API_URL = getApiBaseUrl();

export function getStoredAuthToken() {
  if (typeof window === 'undefined') {
    return '';
  }

  try {
    return String(window.localStorage.getItem('authToken') || '').trim();
  } catch (error) {
    return '';
  }
}

export function buildKioskAuthHeaders(headers = undefined) {
  const merged = new Headers(headers || {});
  const token = getStoredAuthToken();
  if (token && !merged.has('Authorization')) {
    merged.set('Authorization', `Bearer ${token}`);
  }
  const binding = readWorkstationBinding();
  if (binding && token) merged.set('X-BE-Device', binding.token);
  return merged;
}

function normalizeCandidateMediaPath(value) {
  if (!value) {
    return '';
  }

  if (value.startsWith('/')) {
    return value;
  }

  try {
    const url = new URL(value);
    const api = new URL(API_URL);
    const sameHost = url.hostname === api.hostname && String(url.port || '') === String(api.port || '');
    const legacyBeborneHost = ['beborne.com', 'www.beborne.com'].includes(url.hostname.toLowerCase());
    if ((sameHost || legacyBeborneHost) && url.pathname.startsWith('/uploads/')) {
      return url.pathname;
    }
    return '';
  } catch (error) {
    return '';
  }
}

export function resolveMediaUrl(value, options = {}) {
  if (!value) {
    return '';
  }

  const apiUrl = API_URL;
  const {
    width,
    height,
    quality,
    fit,
  } = options || {};
  const normalizedPath = normalizeCandidateMediaPath(value);
  const shouldOptimize = normalizedPath && (width || height || quality || fit);

  if (shouldOptimize) {
    const params = new URLSearchParams({ src: normalizedPath });
    if (width) {
      params.set('w', String(width));
    }
    if (height) {
      params.set('h', String(height));
    }
    if (quality) {
      params.set('q', String(quality));
    }
    if (fit) {
      params.set('fit', String(fit));
    }
    return `${apiUrl}/api/media/asset?${params.toString()}`;
  }

  // Media saved before the BECLICK domain existed can still point to
  // http://beborne.com. Serve those files through the current HTTPS origin so
  // browsers do not block them as mixed content.
  if (normalizedPath) {
    return `${apiUrl}${normalizedPath}`;
  }

  if (value.startsWith('/')) {
    return `${apiUrl}${value}`;
  }

  try {
    const url = new URL(value);
    if (url.hostname === 'localhost' || url.hostname === '127.0.0.1') {
      const api = new URL(apiUrl);
      url.protocol = api.protocol;
      url.hostname = api.hostname;
      url.port = api.port;
      return url.toString();
    }
    return value;
  } catch (error) {
    return value;
  }
}

export function getDefaultBrandLogoUrl(options = {}) {
  const params = new URLSearchParams({ v: 'be-suite-compact-v5' });
  if (options.width) {
    params.set('w', String(options.width));
  }
  if (options.height) {
    params.set('h', String(options.height));
  }
  if (options.quality) {
    params.set('q', String(options.quality));
  }
  if (options.fit) {
    params.set('fit', String(options.fit));
  }
  const query = params.toString();
  return `${API_URL}/api/media/default-brand-logo${query ? `?${query}` : ''}`;
}
